import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  BarChart, Bar, LineChart, Line, ComposedChart, Area,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  PieChart, Pie, Cell,
} from "recharts";
import SectionHeader from "../components/ui/SectionHeader";
import ChartCard from "../components/ui/ChartCard";
import MetricCard from "../components/ui/MetricCard";
import GaugeCard from "../components/ui/GaugeCard";
import ChartTooltip from "../components/ui/ChartTooltip";
import {
  Zap, Droplets, Wifi, AlertTriangle, CheckCircle,
  ChevronDown, ChevronUp, Radio, Activity, Battery,
} from "lucide-react";
import { googleColor } from "../data/googleColors";
import { chartColor, gridProps, axisTick, legendStyle } from "../data/chartTheme";
import { STATUS, statusHex, statusClasses, thresholdStatus } from "../data/statusColors";
import { useTranslate } from "../i18n/useTranslate";

// ── inline data ──────────────────────────────────────────────────
const POWER_ZONES = [
  { zone:"Ramkund Ghat",    substation:"MSEDCL Sub-A", load:88, capacity:100, dgBackup:"DG-R1 Armed", ups:"UPS-R OK", lightPoles:240, litPoles:236, alerts:1, status:"Monitor" },
  { zone:"Panchvati",       substation:"MSEDCL Sub-B", load:74, capacity:100, dgBackup:"DG-P1 Armed", ups:"UPS-P OK", lightPoles:180, litPoles:180, alerts:0, status:"OK"      },
  { zone:"Trimbakeshwar",   substation:"MSEDCL Sub-C", load:68, capacity:100, dgBackup:"DG-T1 Armed", ups:"UPS-T OK", lightPoles:320, litPoles:318, alerts:0, status:"OK"      },
  { zone:"Nashik Road",     substation:"MSEDCL Sub-D", load:72, capacity:100, dgBackup:"DG-N1 Armed", ups:"UPS-N OK", lightPoles:210, litPoles:208, alerts:0, status:"OK"      },
  { zone:"Gangapur Camp",   substation:"MSEDCL Sub-E", load:62, capacity:100, dgBackup:"DG-G1 Armed", ups:"UPS-G OK", lightPoles:160, litPoles:160, alerts:0, status:"OK"      },
  { zone:"Kalaram Temple",  substation:"MSEDCL Sub-F", load:80, capacity:100, dgBackup:"DG-K1 Armed", ups:"UPS-K OK", lightPoles:140, litPoles:138, alerts:1, status:"Monitor" },
];

const WATER_NETWORK = [
  { station:"Pump Stn 1 — Gangapur Dam",  pressure:3.8, capacity:100, flow:92, storage:88,  status:"OK",      tankers:4  },
  { station:"Pump Stn 2 — Panchvati",     pressure:3.4, capacity:100, flow:86, storage:72,  status:"OK",      tankers:6  },
  { station:"Pump Stn 3 — Ramkund",       pressure:2.8, capacity:100, flow:94, storage:64,  status:"Monitor", tankers:8  },
  { station:"Pump Stn 4 — Trimbak Zone",  pressure:3.2, capacity:100, flow:78, storage:80,  status:"OK",      tankers:5  },
  { station:"Pump Stn 5 — Nashik Road",   pressure:3.0, capacity:100, flow:82, storage:76,  status:"OK",      tankers:7  },
  { station:"Pump Stn 6 — Camp Network",  pressure:2.6, capacity:100, flow:88, storage:58,  status:"Alert",   tankers:10 },
  { station:"Pump Stn 7 — Kalaram",       pressure:3.6, capacity:100, flow:76, storage:84,  status:"OK",      tankers:3  },
  { station:"Pump Stn 8 — Panchvati N",   pressure:1.8, capacity:100, flow:62, storage:44,  status:"Alert",   tankers:12 },
];

const COMMS_TOWERS = [
  { tower:"BSNL TK-01 — Ramkund",       type:"4G",    uptime:99.8, load:78, backup:"VSAT",   status:"Live"    },
  { tower:"BSNL TK-02 — Panchvati",     type:"4G",    uptime:99.6, load:72, backup:"VSAT",   status:"Live"    },
  { tower:"BSNL TK-03 — Trimbak",       type:"4G",    uptime:99.2, load:68, backup:"VSAT",   status:"Live"    },
  { tower:"BSNL TK-04 — Nashik Rd",     type:"4G",    uptime:98.8, load:64, backup:"MW Link",status:"Live"    },
  { tower:"Maha Kumbh AI VSAT-01 — EOC",        type:"VSAT",  uptime:100,  load:42, backup:"None",   status:"Live"    },
  { tower:"Maha Kumbh AI VSAT-02 — Camp HQ",    type:"VSAT",  uptime:99.9, load:38, backup:"None",   status:"Live"    },
  { tower:"Police Trunking Network",     type:"P25",   uptime:99.4, load:82, backup:"DMR",    status:"Live"    },
  { tower:"Health Radio Network",        type:"VHF",   uptime:99.1, load:58, backup:"Satellite",status:"Live"  },
];

const BRIDGES = [
  { name:"Godavari Bridge (Main)",       load:88, capacity:100, maxLoad:500, currVehicles:440, structScore:82, lastInspect:"07 Jun 2027", alert:"Monitor — >80% load" },
  { name:"Trimbak Naka Overpass",        load:64, capacity:100, maxLoad:300, currVehicles:192, structScore:91, lastInspect:"08 Jun 2027", alert:"OK" },
  { name:"Panchvati Foot Bridge",        load:72, capacity:100, maxLoad:200, currVehicles:144, structScore:88, lastInspect:"08 Jun 2027", alert:"OK" },
  { name:"Nashik Road Flyover",          load:54, capacity:100, maxLoad:400, currVehicles:216, structScore:94, lastInspect:"09 Jun 2027", alert:"OK" },
  { name:"Gangapur Causeway",            load:46, capacity:100, maxLoad:150, currVehicles:69,  structScore:86, lastInspect:"07 Jun 2027", alert:"OK" },
];

const POWER_TREND = [
  { time:"06:00", totalLoad:58, ramkund:52, panchvati:48, trimbak:44 },
  { time:"08:00", totalLoad:68, ramkund:62, panchvati:58, trimbak:52 },
  { time:"10:00", totalLoad:78, ramkund:74, panchvati:68, trimbak:60 },
  { time:"12:00", totalLoad:82, ramkund:82, panchvati:72, trimbak:64 },
  { time:"14:00", totalLoad:84, ramkund:88, panchvati:74, trimbak:68 },
  { time:"16:00", totalLoad:88, ramkund:92, panchvati:78, trimbak:72 },
  { time:"18:00", totalLoad:86, ramkund:90, panchvati:80, trimbak:70 },
];

const BACKUP_SYSTEMS = [
  { system:"DG-R1 Ramkund",   fuel:74, runtime:"8.4h", kva:500,  lastTest:"09 Jun 12:00", status:"Armed"   },
  { system:"DG-P1 Panchvati", fuel:82, runtime:"9.8h", kva:400,  lastTest:"09 Jun 12:00", status:"Armed"   },
  { system:"DG-T1 Trimbak",   fuel:68, runtime:"7.6h", kva:750,  lastTest:"08 Jun 18:00", status:"Armed"   },
  { system:"DG-N1 Nashik Rd", fuel:88, runtime:"10.4h",kva:350,  lastTest:"09 Jun 08:00", status:"Armed"   },
  { system:"VSAT EOC",        fuel:100,runtime:"72h",  kva:null, lastTest:"09 Jun 06:00", status:"Live"    },
  { system:"EOC UPS Array",   fuel:92, runtime:"4h",   kva:200,  lastTest:"09 Jun 06:00", status:"Live"    },
];

const INFRA_SUMMARY = [
  { label:"Power Grid",   value:78, color:"#F59E0B" },
  { label:"Water Supply", value:82, color:"#0891B2" },
  { label:"Comms",        value:94, color:"#7C3AED" },
  { label:"Bridges",      value:88, color:"#10B981" },
];

const INFRA_STATUS_BUCKET = { OK: "good", Monitor: "warning", Alert: "critical" };
const stColor = s => `badge ${statusClasses(INFRA_STATUS_BUCKET[s] || "info")}`;

// Load/fuel percentage -> bucket bands shared by the power/bridge/backup bars.
const LOAD_BANDS = [
  { min: 85, bucket: "critical" },
  { min: 70, bucket: "warning" },
  { min: 0,  bucket: "good" },
];
const FUEL_BANDS = [
  { min: 70, bucket: "good" },
  { min: 50, bucket: "warning" },
  { min: 0,  bucket: "critical" },
];

// ── component ────────────────────────────────────────────────────
export default function Infrastructure() {
  const [expandedPower, setExpandedPower] = useState(null);
  const t = useTranslate();

  // Computed directly from the arrays rendered below, instead of separately
  // hand-typed numbers that can drift from what the tables actually show.
  const dgArmedCount   = BACKUP_SYSTEMS.filter(b => b.system.startsWith("DG") && b.status === "Armed").length;
  const avgWaterBar    = (WATER_NETWORK.reduce((s, w) => s + w.pressure, 0) / WATER_NETWORK.length).toFixed(1);
  const avgCommsUptime = (COMMS_TOWERS.reduce((s, c) => s + c.uptime, 0) / COMMS_TOWERS.length).toFixed(1);
  const litTotal       = POWER_ZONES.reduce((s, z) => s + z.litPoles, 0);
  const poleTotal      = POWER_ZONES.reduce((s, z) => s + z.lightPoles, 0);
  const lightGridPct   = ((litTotal / poleTotal) * 100).toFixed(1);
  const bridgesOkCount = BRIDGES.filter(b => b.alert === "OK").length;
  const waterAlertCount= WATER_NETWORK.filter(w => w.status === "Alert").length;
  const vsatLiveCount  = COMMS_TOWERS.filter(c => c.type === "VSAT" && c.status === "Live").length;
  const monitoredZones = POWER_ZONES.filter(z => z.status === "Monitor");
  const monitoredBridge= BRIDGES.find(b => b.load >= 80);

  return (
    <div className="space-y-8">
      <SectionHeader icon="Server" title="Critical Infrastructure Resilience"
        subtitle="Power grid · water supply network · comms backbone · bridge structural monitoring · backup systems · DG runtime · lighting grid — Maha AI · Simhastha Kumbh 2027"
        badge="2 ALERTS"
      />

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-8 gap-3">
        <MetricCard title="Power Grid"       value={`${INFRA_SUMMARY.find(s => s.label === "Power Grid").value}%`}  icon="Zap"       color="amber"  index={0} live />
        <MetricCard title="DG Backup"        value={`${dgArmedCount} Armed`} icon="Battery" color="green"  index={1} />
        <MetricCard title="Water Pressure"   value={`${avgWaterBar} bar`} icon="Droplets" color="blue"  index={2} live />
        <MetricCard title="Comms Uptime"     value={`${avgCommsUptime}%`} icon="Wifi"     color="green"  index={3} />
        <MetricCard title="Bridges OK"       value={`${bridgesOkCount} / ${BRIDGES.length}`} icon="GitBranch" color="green"  index={4} />
        <MetricCard title="Light Grid"       value={`${lightGridPct}%`} icon="Sun"      color="green"  index={5} />
        <MetricCard title="Water Alerts"     value={waterAlertCount.toString()}    icon="AlertTriangle" color="red" index={6} live />
        <MetricCard title="VSAT Links"       value={`${vsatLiveCount} Live`} icon="Radio"   color="green"  index={7} />
      </div>

      {/* Infra Banner */}
      <motion.div initial={{ opacity:0, y:-4 }} animate={{ opacity:1, y:0 }}
        className="rounded-2xl bg-white border border-slate-200 shadow-card p-5">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <div className="w-2 h-2 rounded-full bg-teal-400 live-dot" />
              <span className="text-[10px] font-black text-saffron-dark tracking-widest">
                {t("MAHA AI — INFRASTRUCTURE RESILIENCE COMMAND · 6 ZONES · 2 ALERTS")} — 14:35 IST
              </span>
            </div>
            <h3 className="text-base font-black text-slate-900">{t("Critical Infrastructure — Simhastha 2027")}</h3>
            <p className="text-xs text-slate-500 mt-1 max-w-2xl">
              {t("Power")}: {monitoredZones.length} {t("zones monitored (load >80%)")} · {t("Water")}: {waterAlertCount} {t("station alerts")} · {t("Comms")}: {COMMS_TOWERS.length} {t("towers live")}, {avgCommsUptime}% {t("uptime")} · {t("Bridges")}: {monitoredBridge ? 1 : 0} {t("monitored")} {monitoredBridge ? `(${monitoredBridge.load}% ${t("load")})` : ""} · {dgArmedCount} {t("DG backups armed")} · {t("VSAT EOC live")}
            </p>
          </div>
          <div className="grid grid-cols-2 gap-2">
            {INFRA_SUMMARY.map((s, i) => {
              const c = googleColor(i);
              return (
                <div key={s.label}
                  className="rounded-xl px-3 py-2 text-center shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-200"
                  style={{ background:c.bgLight, border:`1px solid ${c.border}` }}>
                  <div className="text-[8px] mb-0.5 font-black uppercase tracking-wide" style={{ color:c.text }}>{t(s.label)}</div>
                  <div className="text-sm font-black" style={{ color:c.text }}>
                    {s.value}%
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </motion.div>

      {/* Gauge row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {INFRA_SUMMARY.map((s, i) => (
          <GaugeCard key={s.label} title={`${s.label} Health`} value={s.value} subtitle="Overall score" index={i} />
        ))}
      </div>

      {/* Power Grid */}
      <ChartCard title="Power Grid — Zone Status"
        subtitle="MSEDCL substation load · DG backup armed · lighting poles — all 6 zones">
        <div className="overflow-x-auto">
          <table className="gov-table w-full text-xs">
            <thead>
              <tr>
                <th className="text-left">{t("Zone")}</th>
                <th className="text-left">{t("Substation")}</th>
                <th>{t("Load")}</th>
                <th className="text-center">{t("DG Backup")}</th>
                <th className="text-center">{t("Lighting")}</th>
                <th className="text-center">{t("Alerts")}</th>
                <th>{t("Status")}</th>
              </tr>
            </thead>
            <tbody>
              {POWER_ZONES.map((z, i) => {
                const litPct = Math.round((z.litPoles / z.lightPoles) * 100);
                return (
                  <motion.tr key={z.zone} initial={{ opacity:0 }} animate={{ opacity:1 }} transition={{ delay: i * 0.06 }}>
                    <td className="font-black text-slate-900">
                      <Link to="/crowd-intelligence" className="hover:text-blue-700 hover:underline underline-offset-2">{t(z.zone)}</Link>
                    </td>
                    <td className="text-[10px] text-slate-500">{z.substation}</td>
                    <td>
                      <div className="flex items-center gap-1.5">
                        <div className="w-16 h-2 bg-slate-100 rounded-full overflow-hidden">
                          <motion.div className={`h-2 rounded-full ${STATUS[thresholdStatus(z.load, LOAD_BANDS)].bar}`}
                            initial={{ width:0 }} animate={{ width:`${z.load}%` }} transition={{ duration:0.8 }} />
                        </div>
                        <span className="font-black text-[10px]">{z.load}%</span>
                      </div>
                    </td>
                    <td className="text-center text-[9px] text-emerald-700 font-bold">{z.dgBackup}</td>
                    <td className="text-center">
                      <span className={`text-[10px] font-black ${STATUS[thresholdStatus(litPct, [{ min:100, bucket:"good" }, { min:0, bucket:"warning" }])].text}`}>
                        {z.litPoles}/{z.lightPoles} ({litPct}%)
                      </span>
                    </td>
                    <td className="text-center">
                      <span className={`font-black ${STATUS[z.alerts > 0 ? "critical" : "good"].text}`}>{z.alerts > 0 ? `⚠ ${z.alerts}` : "✓"}</span>
                    </td>
                    <td><span className={stColor(z.status)}>{t(z.status)}</span></td>
                  </motion.tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </ChartCard>

      {/* Power Load Trend */}
      <ChartCard title="Power Load Trend — Zones (% of Capacity)"
        subtitle="Hourly load tracking — alert triggers if any zone exceeds 90%">
        <ResponsiveContainer width="100%" height={220}>
          <LineChart data={POWER_TREND} margin={{ top:5, right:10, left:-10, bottom:0 }}>
            <CartesianGrid {...gridProps} />
            <XAxis dataKey="time" tick={axisTick} />
            <YAxis domain={[0,100]} unit="%" tick={axisTick} />
            <Tooltip content={<ChartTooltip />} />
            <Legend wrapperStyle={legendStyle} />
            <Line dataKey="totalLoad"  stroke={chartColor(0)} strokeWidth={2.5} dot={false} name={t("Total")} />
            <Line dataKey="ramkund"    stroke={chartColor(1)} strokeWidth={2}   dot={false} name={t("Ramkund")} />
            <Line dataKey="panchvati"  stroke={chartColor(2)} strokeWidth={1.5} dot={false} name={t("Panchvati")} />
            <Line dataKey="trimbak"    stroke={chartColor(3)} strokeWidth={1.5} dot={false} name={t("Trimbak")} />
          </LineChart>
        </ResponsiveContainer>
      </ChartCard>

      {/* Water Network */}
      <ChartCard title="Water Supply Network — 8 Pumping Stations"
        subtitle="Pressure · flow rate · storage level · tankers assigned per station">
        <div className="space-y-2">
          {WATER_NETWORK.map((w, i) => {
            const wBucket = w.status === "Alert" ? "critical" : w.status === "Monitor" ? "warning" : null;
            return (
            <motion.div key={w.station}
              initial={{ opacity:0, y:3 }} animate={{ opacity:1, y:0 }} transition={{ delay: i * 0.06 }}
              className={`p-3 rounded-xl border ${
                wBucket ? `${STATUS[wBucket].border} ${STATUS[wBucket].bg}/20` : "border-slate-200"
              }`}
            >
              <div className="flex items-center gap-3 flex-wrap">
                <div className="flex-1 min-w-[200px]">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <Droplets size={12} className="text-blue-500" />
                    <span className="text-xs font-black text-slate-900">{t(w.station)}</span>
                    <span className={stColor(w.status)}>{t(w.status)}</span>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-1">
                    {[
                      { label:"Pressure", val:`${w.pressure} bar`, color: statusHex(w.pressure < 2.5 ? "critical" : "good") },
                      { label:"Flow",     val:`${w.flow}%`,        color: statusHex(w.flow < 70 ? "warning" : "info") },
                      { label:"Storage",  val:`${w.storage}%`,     color: statusHex(w.storage < 60 ? "critical" : "good") },
                    ].map(m => (
                      <div key={m.label}>
                        <div className="text-[8px] text-slate-400">{t(m.label)}</div>
                        <div className="text-[11px] font-black" style={{ color:m.color }}>{m.val}</div>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="text-center flex-shrink-0">
                  <div className="text-xl font-black text-blue-700">{w.tankers}</div>
                  <div className="text-[9px] text-slate-500">{t("tankers")}</div>
                </div>
              </div>
            </motion.div>
            );
          })}
        </div>
      </ChartCard>

      {/* Comms + Bridges */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ChartCard title="Communications Backbone — Tower Status"
          subtitle="BSNL 4G · Maha Kumbh AI VSAT · Police P25 · Health VHF — uptime and load">
          <div className="space-y-2">
            {COMMS_TOWERS.map((t2, i) => (
              <motion.div key={t2.tower}
                initial={{ opacity:0, x:-4 }} animate={{ opacity:1, x:0 }} transition={{ delay: i * 0.05 }}
                className="flex items-center gap-3 p-2.5 rounded-xl border border-slate-200 hover:bg-slate-50 transition-colors"
              >
                <div className="w-7 h-7 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
                  <Wifi size={12} className="text-blue-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-bold text-slate-800 truncate">{t2.tower}</div>
                  <div className="flex items-center gap-2 text-[9px] text-slate-500">
                    <span className="font-black text-blue-700">{t2.type}</span>
                    <span>{t("Load")}: {t2.load}%</span>
                    <span>{t("Backup")}: {t(t2.backup)}</span>
                  </div>
                </div>
                <div className="text-right flex-shrink-0">
                  <div className="text-[10px] font-black text-emerald-600">{t2.uptime}%</div>
                  <span className="badge badge-live text-[8px]"><span className="w-1.5 h-1.5 rounded-full bg-emerald-500 live-dot mr-1" />{t("Live")}</span>
                </div>
              </motion.div>
            ))}
          </div>
        </ChartCard>

        <ChartCard title="Bridge Structural Monitor"
          subtitle="Load vs capacity · structural integrity score · last inspection date">
          <div className="space-y-3">
            {BRIDGES.map((b, i) => (
              <motion.div key={b.name}
                initial={{ opacity:0, x:4 }} animate={{ opacity:1, x:0 }} transition={{ delay: i * 0.07 }}
                className={`p-3 rounded-xl border ${b.load >= 80 ? `${STATUS.warning.border} ${STATUS.warning.bg}/20` : "border-slate-200"}`}
              >
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="flex-1 min-w-[160px]">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className="text-xs font-black text-slate-900">{t(b.name)}</span>
                      {b.load >= 80 && <AlertTriangle size={11} className="text-amber-500" />}
                    </div>
                    <div className="flex items-center gap-2 mb-1">
                      <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                        <motion.div className={`h-2 rounded-full ${STATUS[thresholdStatus(b.load, LOAD_BANDS)].bar}`}
                          initial={{ width:0 }} animate={{ width:`${b.load}%` }} transition={{ duration:0.8 }} />
                      </div>
                      <span className="text-[10px] font-black">{b.load}%</span>
                    </div>
                    <div className="text-[9px] text-slate-500">{t(b.alert)}</div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <div className="text-lg font-black text-blue-700">{b.structScore}</div>
                    <div className="text-[9px] text-slate-500">{t("struct. score")}</div>
                    <div className="text-[8px] text-slate-400 mt-0.5">{b.lastInspect}</div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </ChartCard>
      </div>

      {/* Backup Systems */}
      <ChartCard title="Backup Power Systems — DG & UPS Status"
        subtitle="Diesel generator fuel level · estimated runtime · kVA rating per zone">
        <div className="overflow-x-auto">
          <table className="gov-table w-full text-xs">
            <thead>
              <tr>
                <th className="text-left">{t("System")}</th>
                <th>{t("Fuel Level")}</th>
                <th className="text-center">{t("Est. Runtime")}</th>
                <th className="text-center">{t("kVA")}</th>
                <th className="text-center">{t("Last Test")}</th>
                <th>{t("Status")}</th>
              </tr>
            </thead>
            <tbody>
              {BACKUP_SYSTEMS.map((b, i) => (
                <motion.tr key={b.system} initial={{ opacity:0 }} animate={{ opacity:1 }} transition={{ delay: i * 0.06 }}>
                  <td className="font-bold text-slate-900">{t(b.system)}</td>
                  <td>
                    <div className="flex items-center gap-2">
                      <div className="w-20 h-2 bg-slate-100 rounded-full overflow-hidden">
                        <motion.div className={`h-2 rounded-full ${STATUS[thresholdStatus(b.fuel, FUEL_BANDS)].bar}`}
                          initial={{ width:0 }} animate={{ width:`${b.fuel}%` }} transition={{ duration:0.8 }} />
                      </div>
                      <span className="font-black text-[10px]">{b.fuel}%</span>
                    </div>
                  </td>
                  <td className="text-center font-bold text-blue-700">{b.runtime}</td>
                  <td className="text-center text-slate-600">{b.kva ? `${b.kva} kVA` : "—"}</td>
                  <td className="text-center font-mono text-[9px] text-slate-400">{b.lastTest}</td>
                  <td><span className={`badge ${statusClasses(b.status === "Armed" ? "warning" : "good")}`}>{t(b.status)}</span></td>
                </motion.tr>
              ))}
            </tbody>
          </table>
        </div>
      </ChartCard>
    </div>
  );
}
